import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, List, ListItem, ListItemButton, ListItemText, Paper, Typography } from "@mui/material";
import CustomTextField from "../CustomTextField";
import DraggableMap from "./DraggableMap";
import { COLORS } from "../../assets/theme";

type AddressWithMapFieldProps = {
  value: any;
  onChange: (v: any) => void;
  helpers?: {
    setFieldValue: (name: string, value: any) => void;
    values?: Record<string, any>;
  };
  defaultLat?: number;
  defaultLng?: number;
  countryCodes?: string; // comma separated country codes
};

const AddressWithMapField: React.FC<AddressWithMapFieldProps> = ({
  value,
  onChange,
  helpers,
  defaultLat = 33.8886,
  defaultLng = 35.4955,
  countryCodes = "lb",
}) => {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const googleReady = useRef<boolean>(false);

  const latNum = helpers?.values?.latitude
    ? Number(helpers?.values?.latitude)
    : undefined;
  const lngNum = helpers?.values?.longitude
    ? Number(helpers?.values?.longitude)
    : undefined;

  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return;

    if (
      (window as any).google &&
      (window as any).google.maps &&
      (window as any).google.maps.places
    ) {
      googleReady.current = true;
      return;
    }

    const scriptId = "google-maps-js";
    if (document.getElementById(scriptId)) {
      const check = setInterval(() => {
        if (
          (window as any).google &&
          (window as any).google.maps &&
          (window as any).google.maps.places
        ) {
          googleReady.current = true;
          clearInterval(check);
        }
      }, 200);
      return () => clearInterval(check);
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&language=en`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      googleReady.current = true;
    };
    script.onerror = () => {
      googleReady.current = false;
    };
    document.head.appendChild(script);

    return () => {};
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  const triggerGeocode = (v: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const q = String(v || "").trim();
        if (q.length < 2) {
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        }

        const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (key && googleReady.current && (window as any).google) {
          try {
            setLoadingSuggest(true);
            const service = new (
              window as any
            ).google.maps.places.AutocompleteService();
            service.getPlacePredictions(
              {
                input: q,
                componentRestrictions: {
                  country: countryCodes.split(",").map((c) => c.trim()),
                },
                language: "en",
              },
              (preds: any[], status: any) => {
                setLoadingSuggest(false);
                if (
                  status ===
                    (window as any).google.maps.places.PlacesServiceStatus.OK &&
                  Array.isArray(preds)
                ) {
                  setSuggestions(preds);
                  setShowSuggestions(true);
                  if (preds[0] && preds[0].place_id) {
                    const ps = new (
                      window as any
                    ).google.maps.places.PlacesService(
                      document.createElement("div")
                    );
                    ps.getDetails(
                      { placeId: preds[0].place_id },
                      (place: any) => {
                        if (
                          place &&
                          place.geometry &&
                          place.geometry.location
                        ) {
                          const lat = place.geometry.location.lat();
                          const lng = place.geometry.location.lng();
                          helpers?.setFieldValue?.("latitude", String(lat));
                          helpers?.setFieldValue?.("longitude", String(lng));
                        }
                      }
                    );
                  }
                } else {
                  setSuggestions([]);
                  setShowSuggestions(false);
                }
              }
            );
          } catch {
            setLoadingSuggest(false);
          }
          return;
        }

        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=${encodeURIComponent(
          countryCodes
        )}&limit=10&accept-language=en&q=${encodeURIComponent(q)}`;
        setLoadingSuggest(true);
        const resp = await fetch(url, {
          headers: {
            Accept: "application/json",
            "Accept-Language": "en",
            "User-Agent": "PadelFrontendDash/1.0",
          },
        });
        if (!resp.ok) {
          setLoadingSuggest(false);
          return;
        }
        const data = await resp.json();
        if (Array.isArray(data) && data.length > 0) {
          setSuggestions(data);
          setShowSuggestions(true);
          const { lat, lon } = data[0];
          helpers?.setFieldValue?.("latitude", String(lat));
          helpers?.setFieldValue?.("longitude", String(lon));
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch {
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoadingSuggest(false);
      }
    }, 250) as unknown as number;
  };

  const handleSelectSuggestion = (s: any) => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (key && googleReady.current && s.place_id && (window as any).google) {
      const ps = new (window as any).google.maps.places.PlacesService(
        document.createElement("div")
      );
      ps.getDetails({ placeId: s.place_id }, (place: any, status: any) => {
        if (
          status === (window as any).google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          const formatted =
            place.formatted_address || place.name || s.description;
          if (formatted) onChange(formatted);
          if (place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            helpers?.setFieldValue?.("latitude", String(lat));
            helpers?.setFieldValue?.("longitude", String(lng));
          }
        }
      });
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const display =
      s.display_name || s.description || s.name || s.formatted_address || s.value;
    if (display) onChange(display);
    if (s.lat && s.lon) {
      helpers?.setFieldValue?.("latitude", String(s.lat));
      helpers?.setFieldValue?.("longitude", String(s.lon));
    }
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const previewSuggestion = (s: any) => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (key && googleReady.current && s.place_id && (window as any).google) {
      const ps = new (window as any).google.maps.places.PlacesService(
        document.createElement("div")
      );
      ps.getDetails({ placeId: s.place_id }, (place: any, status: any) => {
        if (
          status === (window as any).google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry &&
          place.geometry.location
        ) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          helpers?.setFieldValue?.("latitude", String(lat));
          helpers?.setFieldValue?.("longitude", String(lng));
        }
      });
      return;
    }
    if (s.lat && s.lon) {
      helpers?.setFieldValue?.("latitude", String(s.lat));
      helpers?.setFieldValue?.("longitude", String(s.lon));
    }
  };

  return (
    <Box>
      <CustomTextField
        label="Address"
        value={value || ""}
        onChange={(e: any) => {
          const v = e.target.value;
          onChange(v);
          triggerGeocode(v);
        }}
        onBlur={() => {
          setTimeout(() => setShowSuggestions(false), 150);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setShowSuggestions(true);
          if (value && String(value).trim().length >= 2) {
            triggerGeocode(String(value));
          }
        }}
        fullWidth
      />

      {showSuggestions && (
        <Paper
          square
          elevation={2}
          sx={{ mt: 1, maxHeight: 220, overflow: "auto" }}
        >
          {loadingSuggest && (
            <Box sx={{ display: "flex", alignItems: "center", p: 1 }}>
              <CircularProgress size={18} sx={{ mr: 1 }} />
              <Typography variant="body2">Searching...</Typography>
            </Box>
          )}
          <List dense>
            {suggestions.map((s: any, idx: number) => (
              <ListItem key={`${s.place_id ?? s.osm_id ?? idx}-${idx}`} disablePadding>
                <ListItemButton
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => handleSelectSuggestion(s)}
                  onMouseEnter={() => previewSuggestion(s)}
                >
                  <ListItemText
                    primary={
                      s.display_name ||
                      s.description ||
                      s.name ||
                      s.formatted_address ||
                      s.value
                    }
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {!loadingSuggest && suggestions.length === 0 && (
              <ListItem>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No results
                </Typography>
              </ListItem>
            )}
          </List>
        </Paper>
      )}

      <Box
        sx={{
          mt: 1,
          borderRadius: 2,
          overflow: "hidden",
          border: `1px solid ${COLORS.BorderColor}`,
        }}
      >
        <DraggableMap
          lat={
            Number.isFinite(latNum as number)
              ? (latNum as number)
              : defaultLat
          }
          lng={
            Number.isFinite(lngNum as number)
              ? (lngNum as number)
              : defaultLng
          }
          onChange={async (lat, lng) => {
            helpers?.setFieldValue?.("latitude", String(lat));
            helpers?.setFieldValue?.("longitude", String(lng));
            try {
              const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
              if (key && googleReady.current && (window as any).google) {
                const geocoder = new (window as any).google.maps.Geocoder();
                geocoder.geocode(
                  { location: { lat, lng } },
                  (results: any[]) => {
                    if (results && results[0]) {
                      onChange(results[0].formatted_address);
                    }
                  }
                );
                return;
              }
              const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`;
              const resp = await fetch(url, {
                headers: { Accept: "application/json", "Accept-Language": "en" },
              });
              if (resp.ok) {
                const data = await resp.json();
                const display = (data?.display_name as string) || "";
                if (display) onChange(display);
              }
            } catch {}
          }}
          height={280}
        />
      </Box>
    </Box>
  );
};

export default AddressWithMapField;
